import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";

interface ChecklistToSave {
  cid: string;
  rid: string;
  lbid: string;
  yachtid: string;
  action?: string;
  item?: string;
  url: string;
}

interface IEntrySlice {
  current: string;
  editing: boolean;
  activeAutosave: boolean;
  openedSaveModal: boolean;
  openedRefetchModal: boolean;
  isSaving: boolean;
  lastUpdate: string;
  locked: boolean;
  user: {
    full_name: string;
    rolename: string;
    userid: string;
  } | null;
  you: boolean;
  changed: number | null;
  sendDataOnNewEntry: boolean;
  newChecklistToSave: ChecklistToSave[];
  releaseEntryLock: number;
  noSaves: number;
  lockedByOther: boolean;
}

const initialState: IEntrySlice = {
  current: "",
  editing: false,
  isSaving: false,
  lastUpdate: "",
  activeAutosave: false,
  openedSaveModal: false,
  openedRefetchModal: false,
  locked: false,
  user: null,
  you: false,
  changed: null,
  sendDataOnNewEntry: false,
  newChecklistToSave: [],
  releaseEntryLock: 0,
  noSaves: 0,
  lockedByOther: false,
};

export const entrySlice = createSlice({
  name: "entrySlice",
  initialState,
  reducers: {
    resetNewEntries: state => {
      state.newChecklistToSave = [];
    },

    pushNewEntryActionsData: (state, action: { payload: ChecklistToSave }) => {
      const index = state.newChecklistToSave.findIndex(
        el =>
          el.item === action.payload.item &&
          el.action === action.payload.action,
      );
      if (index !== -1) {
        state.newChecklistToSave[index] = action.payload;
      } else {
        state.newChecklistToSave.push(action.payload);
      }
    },

    initEntryLockData: (
      state,
      action: PayloadAction<IEntrySlice & { success?: boolean }>,
    ) => {
      delete action.payload.success;
      return { ...state, ...action.payload };
    },
    setEntryLockData: (
      state,
      action: PayloadAction<any & { success?: boolean }>,
    ) => {
      delete action.payload.success;
      const { activeAutosave, locked, user, you, changed, lockedByOther } =
        action.payload;

      if (Boolean(lockedByOther) !== state.lockedByOther) {
        state.lockedByOther = lockedByOther;
      }

      state.activeAutosave = activeAutosave;
      state.locked = locked;
      state.user = user;
      state.you = you;
      state.changed = changed;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.editing = action.payload;
    },
    setAutoSaving: (
      state,
      action: PayloadAction<{ isSaving: boolean; isError?: boolean }>,
    ) => {
      if (!action.payload.isSaving && !action.payload.isError) {
        state.lastUpdate = dayjs().format("HH:mm");
      }
      state.isSaving = action.payload.isSaving;
      // disable the feature where auto saves releases adding rows lock
      // state.noSaves += 1;
    },
    setNoSaves: (state, action: PayloadAction<number>) => {
      state.noSaves = action.payload;
    },
    setActiveAutosave: (state, action: PayloadAction<boolean>) => {
      state.activeAutosave = action.payload;
    },
    setOpenedSaveModal: (state, action: PayloadAction<boolean>) => {
      state.openedSaveModal = action.payload;
    },
    setOpenedRefetchModal: (state, action: PayloadAction<boolean>) => {
      state.openedRefetchModal = action.payload;
    },
    sendDataAfterSaving: (state, action: PayloadAction<boolean>) => {
      state.sendDataOnNewEntry = action.payload;
    },
    setCurrent: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
    },
    resetEntryLock: () => initialState,
    setReleaseEntryLock: (state, action: PayloadAction<number>) => {
      state.editing = false;
      state.releaseEntryLock = action.payload;
    },
  },
});

export const {
  initEntryLockData,
  setEntryLockData,
  setAutoSaving,
  setActiveAutosave,
  setOpenedSaveModal,
  setOpenedRefetchModal,
  setEditing,
  resetEntryLock,
  sendDataAfterSaving,
  pushNewEntryActionsData,
  resetNewEntries,
  setCurrent,
  setReleaseEntryLock,
  setNoSaves,
} = entrySlice.actions;

export default entrySlice.reducer;
